import { useState, useEffect, useCallback } from 'react'
import { apiClient } from 'infra/api'
import { enqueueNotification } from 'Utils/Helpers'
import { Button, Label, TextInput, PasswordInput, TargetIcon } from 'procyon-ui'
import React from 'react'

export const useADDomainPasswords = () => {
  const [adPasswords, setAdPasswords] = useState([])
  const [editingRow, setEditingRow] = useState(null)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch list once on mount or on update cycle
  const fetchPasswords = useCallback(async () => {
    try {
      const response = await apiClient('adpasswords').getAll()
      setAdPasswords(response || [])
    } catch (error) {
      setAdPasswords([])
    }
  }, [])

  useEffect(() => {
    fetchPasswords()
  }, [fetchPasswords])

  // Save handler
  const handleSave = async () => {
    if (!editingRow) return

    setIsLoading(true)
    try {
      const { ObjectMeta } = adPasswords.find((r) => r.ObjectMeta.Name === editingRow.id) || {}
      const payload = {
        ObjectMeta: {
          Tenant: ObjectMeta?.Tenant,
          Namespace: ObjectMeta?.Namespace,
          Name: editingRow.name
        },
        UserName: editingRow.username,
        Password: editingRow.password
      }

      await apiClient('adpasswords').update(payload)
      enqueueNotification('Username and Password Updated Successfully!', 'info')
      await fetchPasswords()
      setEditingRow(null)
    } catch {
      // failed — can show an error notification if needed
    } finally {
      setIsLoading(false)
    }
  }

  const handleEdit = (record) => setEditingRow(record)
  const handleCancel = () => setEditingRow(null)

  // Adapt table data with inline editing
  const tableData = adPasswords.map((row) => {
    const base = {
      id: row.ObjectMeta.Name,
      name: row.ObjectMeta.Name,
      username: row.UserName || 'Enter User@domain Name',
      password: row.Password ? '************' : 'Enter Password',
      rotatePasswordEnabled: row.RotatePasswords,
      defaultFor: row?.DefaultFor?.Elems || [],
      raw: row
    }
    if (editingRow && base.id === editingRow.id) {
      return editingRow
    }
    return base
  })

  return {
    tableData,
    editingRow,
    setEditingRow,
    handleEdit,
    handleCancel,
    handleSave,
    isLoading
  }
}

export const columns = ({ onEdit, onCancel, onSave, editingRow, onEditChange }) => [
  {
    dataIndex: 'name',
    title: 'Name'
  },
  {
    dataIndex: 'username',
    title: 'Username',
    render: (value, record) => {
      const isEditing = editingRow && editingRow.id === record.id
      const displayValue = isEditing && record.username === 'Enter User@domain Name' ? '' : value
      const className =
        !isEditing && value === 'Enter User@domain Name' ? 'text-gray-400 italic' : ''

      return isEditing ? (
        <TextInput
          value={displayValue}
          onChange={(e) => onEditChange('username', e.target.value)}
        />
      ) : (
        <div className={className}>{value}</div>
      )
    }
  },
  {
    dataIndex: 'password',
    title: 'Password',
    render: (value, record) => {
      const isEditing = editingRow && editingRow.id === record.id
      const displayValue = isEditing && record.password === 'Enter Password' ? '' : value
      const className = !isEditing && value === 'Enter Password' ? 'text-gray-400 italic' : ''

      return isEditing ? (
        <PasswordInput
          value={displayValue}
          onChange={(e) => onEditChange('password', e.target.value)}
        />
      ) : (
        <div className={className}>{value}</div>
      )
    }
  },
  {
    dataIndex: 'rotatePasswordEnabled',
    title: 'Rotate Password',
    render: (val) =>
      val ? <Label variant='success' text='Enabled' /> : <Label variant='gray' text='Disabled' />
  },
  {
    dataIndex: 'defaultFor',
    title: 'Default For',
    render: (values) => (
      <div className='flex gap-2'>
        {values.map((type) => (
          <TargetIcon key={type} type={type} width='32px' />
        ))}
        {!values.length && <Label variant='gray' text='None' />}
      </div>
    )
  },
  {
    dataIndex: 'actions',
    title: 'Action',
    render: (value, record) => {
      const isEditing = editingRow && editingRow.id === record.id
      return (
        <div className='flex gap-2'>
          {isEditing ? (
            <>
              <Button onClick={onSave} variant='primary'>
                Save
              </Button>
              <Button onClick={onCancel} variant='grayRed'>
                Cancel
              </Button>
            </>
          ) : (
            <Button onClick={() => onEdit(record)} variant='primary'>
              Edit
            </Button>
          )}
        </div>
      )
    }
  }
]
